"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CardProvenanceConflictAck = exports.CardProvenanceConflictOutcome = exports.CardProvenanceConflictV1 = exports.CardProvenanceReplayConflictRule = exports.LINK_PROVENANCE_CONFLICT_MAX = exports.CARD_PROVENANCE_CONFLICT_SCHEMA = exports.LINK_CARD_PROVENANCE_CAPABILITY = void 0;
const zod_1 = require("zod");
const sync_card_provenance_js_1 = require("./sync-card-provenance.js");
/**
 * RL-847 — card provenance at Link stores (Link-store design r5, §2.2 L8,
 * §5.1). Step 2a adds the contract only: nothing serves, sends or reads it
 * until the Link (2b) and Back Office (2c) adopt it.
 *
 * ------------------------------------------------------------------
 * WIRE SHAPE: OUTER ARRAYS LOOSE, ELEMENTS STRICT (the VL-400 pattern)
 * ------------------------------------------------------------------
 * The Link reports the replay conflicts it recorded in
 * `LinkStatusReport.provenanceConflicts`, and Back Office answers each in
 * `LinkStatusResponse.provenanceConflictAcks`. Both outer arrays check only
 * their bound, so one malformed element never refuses the message; each
 * element is parsed on its own against the strict schemas below.
 *
 * Nothing in an element is authority: Back Office takes the tenant, the Link
 * device, its activation and its location from the device token, never from
 * the element. A Link conflict can only withdraw provenance or withhold a
 * ledger, never grant either.
 */
/**
 * The build capability a Link reports in `LinkStatusReport.capabilities` once
 * it has the combined card-provenance route, faithful custody, both replay
 * comparisons and conflict delivery (L1–L8). A build fact: no setting turns
 * it off. Distinct from the till handshake key `sync.card-provenance-v1`.
 */
exports.LINK_CARD_PROVENANCE_CAPABILITY = 'card_provenance_v1';
/** The `schema` literal of a conflict element; a new field comes under a new literal. */
exports.CARD_PROVENANCE_CONFLICT_SCHEMA = 'card_provenance_conflict_v1';
/** Most conflict elements in one report, and most ACKs in one answer. */
exports.LINK_PROVENANCE_CONFLICT_MAX = 50;
/** A lower-case canonical UUID: the Link generates it once, at insert. */
const ConflictId = zod_1.z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, 'a lower-case UUID');
/** An original reference exactly as the till sent it (`originalReceiptId`). */
const WireOriginalReference = zod_1.z.string().min(1).max(256);
/** The replay rules a conflict element can carry: never a V-rule. */
exports.CardProvenanceReplayConflictRule = sync_card_provenance_js_1.CardProvenanceConflictCode.extract([
    'REPLAY_AMENDMENT',
    'REPLAY_CONFLICT',
    'REFUND_MARKER_AMENDMENT',
    'REFUND_MARKER_CONFLICT',
]);
/**
 * One replay conflict the Link recorded (L7), as it delivers it (§5.1).
 * `conflictId` is stable across every resend. A refund element carries the
 * two wire original references when the Link has them, so Back Office can
 * build the refund's target set from a Link-only detection; a refund whose
 * reference cannot be recovered is still delivered without them (r5). A sale
 * element carries neither, and never a refund-marker rule.
 */
exports.CardProvenanceConflictV1 = zod_1.z
    .object({
    schema: zod_1.z.literal(exports.CARD_PROVENANCE_CONFLICT_SCHEMA),
    conflictId: ConflictId,
    /** The uploading till: the record key's terminal, and a refund resolver's caller. */
    originTerminalId: zod_1.z.string().min(1).max(128),
    /** The till's record key. */
    clientUuid: zod_1.z.string().min(1).max(64),
    kind: zod_1.z.enum(['sale', 'refund']),
    rule: exports.CardProvenanceReplayConflictRule,
    /** `provenanceFormHash` of the submitted form (the shared contracts hash). */
    submittedFormHash: zod_1.z.string().regex(/^[0-9a-f]{64}$/, '64 lower-case hex digits'),
    /** Refunds only: the `originalReceiptId` of the row the Link stored first. */
    storedOriginalRef: WireOriginalReference.optional(),
    /** Refunds only: the `originalReceiptId` the replay carried. */
    submittedOriginalRef: WireOriginalReference.optional(),
    detectedAt: zod_1.z.string().datetime(),
})
    .strict()
    .superRefine((element, ctx) => {
    if (element.kind === 'refund')
        return;
    if (element.rule === 'REFUND_MARKER_AMENDMENT' || element.rule === 'REFUND_MARKER_CONFLICT') {
        ctx.addIssue({ code: 'custom', path: ['rule'], message: 'a refund-marker rule on a sale' });
    }
    for (const key of ['storedOriginalRef', 'submittedOriginalRef']) {
        if (element[key] !== undefined) {
            ctx.addIssue({ code: 'custom', path: [key], message: 'an original reference on a sale' });
        }
    }
});
/**
 * Back Office's answer to one element (§5.1). Every outcome is terminal for
 * the Link:
 * - `applied` — a quarantine row was written, or already existed;
 * - `pending` — the receipt is not on Back Office yet; a pending row was stored;
 * - `duplicate` — this `conflictId` was answered before; `earlierOutcome`
 *   repeats that answer;
 * - `out_of_scope` — the authenticated scope rule failed;
 * - `malformed` — the element failed its strict schema.
 */
exports.CardProvenanceConflictOutcome = zod_1.z.enum([
    'applied',
    'pending',
    'duplicate',
    'out_of_scope',
    'malformed',
]);
exports.CardProvenanceConflictAck = zod_1.z
    .object({
    conflictId: ConflictId,
    outcome: exports.CardProvenanceConflictOutcome,
    /** With `duplicate` only: the outcome this `conflictId` was first answered with. */
    earlierOutcome: exports.CardProvenanceConflictOutcome.exclude(['duplicate']).optional(),
})
    .strict()
    .superRefine((ack, ctx) => {
    if ((ack.outcome === 'duplicate') !== (ack.earlierOutcome !== undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['earlierOutcome'],
            message: 'earlierOutcome goes with a duplicate, and only with one',
        });
    }
});
//# sourceMappingURL=link-card-provenance.js.map